import type { FC } from 'react';
import React from 'react';
import { styled } from '@compiled/react';

import { cssMap, cx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';

import {
	ContentTypesHeaderContainer,
	TitleAndBreadcrumbsWrapper,
} from './contentTypesHeaderStyles.tsx';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Skeleton = styled.div<{ skeletonHeight: string; skeletonWidth: string }>({
	borderRadius: token('radius.full'),
	background: token('color.skeleton'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: ({ skeletonHeight }) => skeletonHeight,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: ({ skeletonWidth }) => skeletonWidth,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BreadcrumbsSeparatorSkeleton = styled.div({
	margin: `0px ${token('space.050')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:before': {
		flexShrink: 0,
		content: '"/"',
		color: token('color.background.accent.gray.subtlest'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SkeletonWrapper = styled.div<{ isTitle?: boolean }>({
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: ({ isTitle }) => (isTitle ? '24px' : '20px'),
});

type ContentTypesHeaderPlaceholderProps = {
	isFloating?: boolean;
	isPlaceholderUiFixEnabled?: boolean;
};

const styles = cssMap({
	flexNavigationContainerPlaceholder: {
		alignItems: 'flex-start',
		display: 'flex',
		flexDirection: 'row',
		flexWrap: 'nowrap',
		boxSizing: 'border-box',
		justifyContent: 'space-between',
		paddingTop: token('space.050'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.150'),
	},
	flexNavigationContainerPlaceholderUiFix: {
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
	},
	titleAndBreadcrumbsPlaceholderUiFix: {
		height: '56px',
		width: '100%',
		paddingInline: token('space.150'),
		paddingBlockStart: token('space.150'),
		paddingBlockEnd: token('space.150'),
		alignItems: 'center',
		gap: token('space.100'),
	},
	titleAndBreadcrumbsPlaceholderFloatingUiFix: {
		height: '58px',
		width: 'auto',
		paddingInline: token('space.100'),
		paddingBlockStart: token('space.100'),
		paddingBlockEnd: token('space.100'),
		marginTop: token('space.100'),
		marginInline: token('space.100'),
	},
});

export const ContentTypesHeaderPlaceholder: FC<ContentTypesHeaderPlaceholderProps> = ({
	isFloating,
	isPlaceholderUiFixEnabled = false,
}) => {
	const breadcrumbsSkeletonHeight = isPlaceholderUiFixEnabled ? '20px' : '12px';
	const titleSkeletonHeight = isPlaceholderUiFixEnabled ? '20px' : '14px';

	return (
		<ContentTypesHeaderContainer isFloating={isFloating}>
			<Box
				xcss={cx(
					styles.flexNavigationContainerPlaceholder,
					isPlaceholderUiFixEnabled && styles.flexNavigationContainerPlaceholderUiFix,
				)}
				testId="content-types-header-placeholder"
			>
				<TitleAndBreadcrumbsWrapper
					xcss={cx(
						isPlaceholderUiFixEnabled && styles.titleAndBreadcrumbsPlaceholderUiFix,
						isPlaceholderUiFixEnabled &&
							isFloating &&
							styles.titleAndBreadcrumbsPlaceholderFloatingUiFix,
					)}
				>
					<SkeletonWrapper>
						<Skeleton skeletonHeight={breadcrumbsSkeletonHeight} skeletonWidth="74px" />
						<BreadcrumbsSeparatorSkeleton data-testid="content-types-header-placeholder-breadcrumbs-separator" />
						<Skeleton
							data-testid="content-types-header-placeholder-breadcrumbs-skeleton"
							skeletonHeight={breadcrumbsSkeletonHeight}
							skeletonWidth="74px"
						/>
					</SkeletonWrapper>
					<SkeletonWrapper isTitle>
						<Skeleton skeletonHeight={titleSkeletonHeight} skeletonWidth="326px" />
					</SkeletonWrapper>
				</TitleAndBreadcrumbsWrapper>
			</Box>
		</ContentTypesHeaderContainer>
	);
};
